import { defineField, defineType } from 'sanity'
import { richTextContent } from '../../data/richText'

export default defineType({
  name: 'infoBar',
  title: 'Info Bar',
  type: 'document',
  fieldsets: [
    {
      name: 'modal',
      title: 'Modal',
      options: {
        collapsed: false,
        collapsible: true,
      },
      hidden: ({ document }) => document?.displayType !== 'modal',
    },
    {
      name: 'link',
      title: 'Link',
      options: {
        collapsed: false,
        collapsible: true,
      },
      hidden: ({ document }) => document?.displayType !== 'link',
    },
  ],
  fields: [
    defineField({
      name: 'title',
      title: 'Title',
      type: 'string',
    }),
    defineField({
      name: 'enable',
      title: 'Enable',
      description: 'Allows you to toggle info bar on and off',
      type: 'boolean',
    }),
    defineField({
      name: 'sticky',
      title: 'Sticky',
      description:
        'Keep the bar pinned to the top of the screen as visitors scroll, on every page. Sticky page elements, such as the homepage vehicle selector or the fitment guide section nav, take its place when they reach the top. When off, the bar is pinned on the homepage only.',
      type: 'boolean',
      initialValue: false,
    }),
    defineField({
      name: 'displayType',
      title: 'Display Type',
      type: 'string',
      initialValue: 'modal',
      options: {
        list: [
          { title: 'Modal', value: 'modal' },
          { title: 'Link', value: 'link' },
        ],
        layout: 'radio',
      },
    }),
    defineField({
      name: 'content',
      title: 'Content',
      type: 'array',
      of: richTextContent,
      fieldset: 'modal',
      description:
        'If content is empty, info bar will not be clickable and modal will not open',
    }),
    defineField({
      name: 'buttons',
      title: 'Buttons',
      type: 'buttonList',
      fieldset: 'modal',
    }),
    defineField({
      name: 'link',
      title: 'Link',
      type: 'infoBarLink',
      fieldset: 'link',
      hidden: ({ document }) => document?.displayType !== 'link',
    }),
    defineField({
      name: 'signedInContent',
      title: 'Signed-in customers',
      description:
        'The content above is what guests see. Choose what customers who are signed in to their account see instead.',
      type: 'string',
      initialValue: 'same',
      options: {
        list: [
          { title: 'Same as guests', value: 'same' },
          { title: 'Different content', value: 'custom' },
          { title: 'Hide the bar', value: 'hidden' },
        ],
        layout: 'radio',
      },
    }),
    defineField({
      name: 'signedIn',
      title: 'Content for signed-in customers',
      type: 'object',
      hidden: ({ document }) => document?.signedInContent !== 'custom',
      options: {
        collapsible: true,
        collapsed: false,
      },
      fields: [
        defineField({
          name: 'title',
          title: 'Title',
          type: 'string',
        }),
        defineField({
          name: 'displayType',
          title: 'Display Type',
          type: 'string',
          initialValue: 'modal',
          options: {
            list: [
              { title: 'Modal', value: 'modal' },
              { title: 'Link', value: 'link' },
            ],
            layout: 'radio',
          },
        }),
        defineField({
          name: 'content',
          title: 'Modal content',
          type: 'array',
          of: richTextContent,
          description:
            'If content is empty, info bar will not be clickable and modal will not open',
          hidden: ({ parent }) => parent?.displayType !== 'modal',
        }),
        defineField({
          name: 'buttons',
          title: 'Modal buttons',
          type: 'buttonList',
          hidden: ({ parent }) => parent?.displayType !== 'modal',
        }),
        defineField({
          name: 'link',
          title: 'Link',
          type: 'infoBarLink',
          hidden: ({ parent }) => parent?.displayType !== 'link',
        }),
      ],
    }),
  ],
  preview: {
    select: {
      title: 'title',
      displayType: 'displayType',
    },
    prepare({ title = 'Untitled', displayType = 'modal' }) {
      return {
        title,
        subtitle: `Info bar (${displayType})`,
      }
    },
  },
})